"use client";

import { useState } from "react";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  ResultCard,
  ScriptTextArea,
} from "@/components/tools/transliteration-more/shared";
import { t } from "@/lib/i18n/interpolate";
import {
  GEORGIAN_SYSTEMS,
  type GeorgianSystem,
  georgianLetterRows,
  latinToGeorgian,
  romanizeGeorgian,
} from "@/lib/tool-kit/transliteration-more/georgian";

/** English strings. A translated content file overrides these through `ui`. */
export const GEORGIAN_TRANSLITERATOR_STRINGS = {
  direction: "Direction",
  toLatin: "Georgian → Latin",
  toGeorgian: "Latin → Georgian",
  inputLabel: "Georgian text",
  inputHint:
    "Modern Georgian (Mkhedruli). The older Nuskhuri and Asomtavruli letters and the modern capital forms are read too.",
  latinInputLabel: "Latin text",
  latinInputHint:
    "Written in the system you choose below. Apostrophes can be straight or curly, and a hyphen keeps two letters apart (s-h is სჰ, not შ).",
  system: "System to read",
  samples: {
    one: "საქართველო, თბილისი, ქუთაისი",
    two: "შოთა რუსთაველი, ვეფხისტყაოსანი",
    national: "Sakartvelo, Tbilisi, Kutaisi",
    iso: "sak̕art̕velo, t̕bilisi, k̕ut̕aisi",
    "ala-lc": "Sakʻartʻvelo, Tʻbilisi, Kʻutʻaisi",
  },
  sampleOne: "Place names",
  sampleTwo: "A poet and his poem",
  sampleLatin: "Insert a sample",
  optionsLegend: "Options",
  capitalize:
    "Capitalize each word (national system and ALA-LC; ISO 9984 has no capitals)",
  resultsHeading: "Romanized",
  georgianHeading: "Georgian",
  systems: {
    national: "National system (2002)",
    iso: "ISO 9984:1996",
    "ala-lc": "ALA-LC (Library of Congress)",
  },
  uses: {
    national:
      "Georgia's national system, also used by BGN/PCGN since 2009. Apostrophes mark the ejectives; digraphs sh, zh, gh, kh, dz.",
    iso: "One Latin letter per Georgian letter, no digraphs, so it can be reversed exactly.",
    "ala-lc":
      "Library catalogs and most English-language reference lists. Diacritics and the sign ʻ for aspirates.",
  },
  georgianUse: "Georgian written from the {system} text above.",
  enterText: "Type or paste some text to convert.",
  unknownChars:
    "Left unchanged because the system has no value for them: {chars}.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Letter by letter",
  letterColumn: "Letter",
  nationalColumn: "National (2002)",
  isoColumn: "ISO 9984",
  alaColumn: "ALA-LC",
  archaicRows: "Archaic and dialect letters",
  modernRows: "Modern alphabet (Mkhedruli)",
  tableNote:
    "In the national system an apostrophe marks the ejective consonants კ პ ტ ყ წ ჭ, and the aspirated თ ფ ქ ც ჩ are written without one. ISO 9984 does the opposite with the mark: it goes on the aspirates (U+0315), not the ejectives. A blank cell means the system has no value for the letter.",
} as const;

type Strings = typeof GEORGIAN_TRANSLITERATOR_STRINGS;
type Direction = "to-latin" | "to-georgian";

function LetterTable({ strings }: { strings: Strings }) {
  const rows = georgianLetterRows();
  const groups = [
    { key: "modern", title: strings.modernRows, archaic: false },
    { key: "archaic", title: strings.archaicRows, archaic: true },
  ];
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      {groups.map((group) => (
        <div className="space-y-2" key={group.key}>
          <p className={LABEL}>{group.title}</p>
          <DataTable
            head={[
              strings.letterColumn,
              strings.nationalColumn,
              strings.isoColumn,
              strings.alaColumn,
            ]}
            rows={rows
              .filter((row) => row.archaic === group.archaic)
              .map((row) => [row.georgian, row.national, row.iso, row.alaLc])}
          />
        </div>
      ))}
      <p className={HINT}>{strings.tableNote}</p>
    </section>
  );
}

function ToLatinResults({
  text,
  capitalize,
  strings,
}: {
  text: string;
  capitalize: boolean;
  strings: Strings;
}) {
  const results = GEORGIAN_SYSTEMS.map((system) => ({
    system,
    result: romanizeGeorgian(text, system, { capitalize }),
  }));
  const unknown = [
    ...new Set(
      results
        .filter((each) => each.system !== "ala-lc")
        .flatMap((each) => each.result.unknown)
    ),
  ];
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {unknown.length > 0 ? (
        <Notice>{t(strings.unknownChars, { chars: unknown.join(" ") })}</Notice>
      ) : null}
      <div className="grid gap-4 md:grid-cols-3">
        {results.map(({ system, result }) => (
          <ResultCard
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            key={system}
            lang="ka-Latn"
            text={result.text}
            title={strings.systems[system]}
            use={strings.uses[system]}
          />
        ))}
      </div>
    </section>
  );
}

function ToGeorgianResults({
  text,
  system,
  strings,
}: {
  text: string;
  system: GeorgianSystem;
  strings: Strings;
}) {
  const result = latinToGeorgian(text, system);
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.georgianHeading}</h2>
      {result.unknown.length > 0 ? (
        <Notice>
          {t(strings.unknownChars, { chars: result.unknown.join(" ") })}
        </Notice>
      ) : null}
      <ResultCard
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        lang="ka"
        text={result.text}
        title={strings.systems[system]}
        use={t(strings.georgianUse, { system: strings.systems[system] })}
      />
    </section>
  );
}

export default function GeorgianTransliteratorWidget() {
  const strings = useToolUi(GEORGIAN_TRANSLITERATOR_STRINGS);
  const [direction, setDirection] = useState<Direction>("to-latin");
  const [system, setSystem] = useState<GeorgianSystem>("national");
  const [text, setText] = useState<string>(strings.samples.one);
  const [latin, setLatin] = useState<string>(strings.samples.national);
  const [capitalize, setCapitalize] = useState(false);
  const forward = direction === "to-latin";
  const current = forward ? text : latin;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.direction}
        name="ka-direction"
        onChange={setDirection}
        options={[
          { value: "to-latin", label: strings.toLatin },
          { value: "to-georgian", label: strings.toGeorgian },
        ]}
        value={direction}
      />
      {forward ? (
        <>
          <ScriptTextArea
            dir="ltr"
            hint={strings.inputHint}
            id="ka-input"
            label={strings.inputLabel}
            lang="ka"
            onChange={setText}
            value={text}
          />
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => setText(strings.samples.one)}
              type="button"
            >
              {strings.sampleOne}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() => setText(strings.samples.two)}
              type="button"
            >
              {strings.sampleTwo}
            </button>
          </div>
          <fieldset className="space-y-3">
            <legend className={LABEL}>{strings.optionsLegend}</legend>
            <Checkbox
              checked={capitalize}
              label={strings.capitalize}
              onChange={setCapitalize}
            />
          </fieldset>
        </>
      ) : (
        <>
          <PillGroup
            label={strings.system}
            name="ka-system"
            onChange={setSystem}
            options={GEORGIAN_SYSTEMS.map((each) => ({
              value: each,
              label: strings.systems[each],
            }))}
            value={system}
          />
          <ScriptTextArea
            dir="ltr"
            hint={strings.latinInputHint}
            id="ka-latin-input"
            label={strings.latinInputLabel}
            lang="ka-Latn"
            onChange={setLatin}
            value={latin}
          />
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => setLatin(strings.samples[system])}
              type="button"
            >
              {strings.sampleLatin}
            </button>
          </div>
        </>
      )}
      {current.trim() === "" ? <Notice>{strings.enterText}</Notice> : null}
      {current.trim() !== "" && forward ? (
        <ToLatinResults capitalize={capitalize} strings={strings} text={text} />
      ) : null}
      {current.trim() !== "" && !forward ? (
        <ToGeorgianResults strings={strings} system={system} text={latin} />
      ) : null}
      <LetterTable strings={strings} />
    </div>
  );
}
